// Logic hiển thị khối "AI đọc cả mùa" ở bước Handoff (260928 E3-P3 K9) — hàm thuần, KHÔNG import antd (antd v6 không load được dưới jest của react-scripts).
// `SeasonReaderCard.tsx` chỉ vẽ lại kết quả của `seasonReaderCardView`; mọi quyết định (nhãn · màu · nút nào bật · nút sửa theo tập nào) nằm ở đây để test được.
//
// Trạng thái HIỆU LỰC (đang đọc / cũ / chưa phủ) do SERVER tính (cần băm kịch bản) — FE không tự suy.
import type { ReaderGateIssue, SeasonReaderViewResponse } from '../../types/adaptation';
import { redactMoney } from './workbench/moneyRedact';
import { formatVnAuditTime, GateColor, dimensionLabelOf } from './gateView';

/** Cổng người đọc và cổng nối tập là HAI cổng — vượt cái này không vượt được cái kia. */
export const CHAIN_NOTE = 'Nối tập là cổng riêng — vượt cổng AI đọc không vượt được chuỗi gãy (RANGE_HANDOFF_NOT_READY).';

/** Viết lại tập đổi đoạn kết ⇒ tập sau lệch chuỗi nối. Hiện trước khi bấm. */
export const REWRITE_WARNING = 'Viết lại một tập có thể đổi đoạn kết của nó — khi đó các tập sau phải viết lại để nối lại chuỗi (cổng "Nối tập" là cổng riêng). Nếu lỗi nằm ở giữa mùa, nên "Viết lại tuần tự từ tập X".';

export type SeasonReaderUiState = SeasonReaderViewResponse['state'];

export interface ReaderRewrite { from: number; to: number; force: true; label: string }

export interface ReaderQuoteView { episode: number; text: string; valid: boolean }

export interface ReaderIssueView {
  id: string;
  /** Tập có lỗi (0 nếu không đọc được `ep:N`). */
  episode: number;
  where: string;
  /** `unit === \`ep:${episode}\`` NGUYÊN VĂN — chỉ khi đúng thế BE mới làm chip (⇒ có id để "Nhờ AI sửa"); tập suy từ evidence thì false. */
  unitExact: boolean;
  dimension: string;
  dimensionLabel: string;
  problem: string;
  quotes: ReaderQuoteView[];
  suggestedFix: string;
  /** "Viết lại tập X" = `{from:X,to:X,force:true}`; không biết X ⇒ null. */
  rewriteOne: ReaderRewrite | null;
  /** "Viết lại tuần tự từ tập X" = `{from:X,to:<tập DRAFT cuối>,force:true}`. */
  rewriteFrom: ReaderRewrite | null;
}

export interface SeasonReaderCardView {
  state: SeasonReaderUiState;
  color: GateColor;
  label: string;
  description: string;
  rangeText: string;
  blocking: ReaderIssueView[];
  advisory: ReaderIssueView[];
  /** Nút "AI đọc cả mùa" / "AI đọc lại". */
  canRun: boolean;
  runLabel: 'AI đọc cả mùa' | 'AI đọc lại';
  /** Nút "Vẫn tiếp tục" (chỉ BLOCKED tươi). */
  canOverride: boolean;
  /** Cổng này ĐANG chặn handoff (server: env bật · chế độ range · có loại chặn · trạng thái chưa qua). */
  blocksHandoff: boolean;
  overrideInfo?: string;
  notes: string[];
  chainNote: string;
  rewriteWarning: string;
}

/** F68 — copy chung cho cả "AI đọc cả mùa" và "AI đọc lại"; cả hai đều đọc lại phạm vi 1..N và tốn AI. */
export const seasonReaderRunConfirmText = (draftMax: number): string =>
  `Chạy nền, tốn AI — AI đọc cả mùa tập 1–${Math.max(1, Math.floor(draftMax) || 1)}.`;

const EP_UNIT = /^ep:(\d+)$/;
const SCRIPT_EP = /^script_ep(\d+)$/;
const isPosInt = (n: unknown): n is number => typeof n === 'number' && Number.isInteger(n) && n >= 1;

const quoteEpisode = (e: ReaderGateIssue['evidence'][number]): number => {
  const loc = e.locator?.episode;
  if (isPosInt(loc)) return loc;
  const m = SCRIPT_EP.exec(e.artifact ?? '');
  return m ? Number(m[1]) : 0;
};

const toIssueView = (i: ReaderGateIssue, draftMax: number): ReaderIssueView => {
  const m = EP_UNIT.exec(i.unit ?? '');
  const quotes = (i.evidence ?? []).map((e) => ({ episode: quoteEpisode(e), text: String(e.quote ?? ''), valid: !!e.valid }));
  const fromQuotes = Math.min(...quotes.map((q) => q.episode).filter(isPosInt), Infinity);
  const episode = m ? Number(m[1]) : fromQuotes;
  const ep = isPosInt(episode) ? episode : 0;
  return {
    id: i.id, episode: ep, unitExact: ep > 0 && i.unit === `ep:${ep}`, where: ep ? `Tập ${ep}` : String(i.unit ?? ''), dimension: i.dimension, dimensionLabel: dimensionLabelOf(i.dimension),
    problem: i.problem, quotes, suggestedFix: i.suggestedFix,
    rewriteOne: ep ? { from: ep, to: ep, force: true, label: `Viết lại tập ${ep}` } : null,
    rewriteFrom: ep ? { from: ep, to: Math.max(draftMax, ep), force: true, label: `Viết lại tuần tự từ tập ${ep}` } : null,
  };
};

/** Trạng thái chưa qua cổng ⇒ nếu server đang chặn handoff thì cổng này CHẶN. */
const OPEN_STATES: ReadonlySet<string> = new Set(['NONE', 'RUNNING', 'NOT_COVERED', 'STALE', 'BLOCKED']);

export const seasonReaderCardView = (r: SeasonReaderViewResponse | null | undefined): SeasonReaderCardView | null => {
  if (!r) return null;
  if (!r.gate && !r.draftMax) return null;   // chưa có kịch bản DRAFT ⇒ chưa có gì để đọc
  const gate = r.gate;
  const drafts = r.draftMax ?? 0;
  const rawBlocking = (gate?.blockingIssues ?? []).map((i) => toIssueView(i, drafts));
  const rawAdvisory = (gate?.advisoryIssues ?? []).map((i) => toIssueView(i, drafts));
  const serverBlocks = r.enabled && r.splitMode === 'range' && (r.blockingTypes ?? []).length > 0;
  const notes: string[] = [];
  if (r.enabled && r.splitMode !== 'range') notes.push('Truyện đang ở chế độ theo chương: người đọc chỉ gợi ý, không chặn handoff (cổng handoff chỉ áp cho chế độ theo đoạn nguồn).');
  // F1: cổng bỏ qua vì đạt giới hạn ⇒ không in `note` BE («…$5/truyện…»); note khác bỏ SỐ TIỀN.
  if (gate?.note && gate.status !== 'RUNNING' && gate.status !== 'SKIPPED_BUDGET' && r.state !== 'SKIPPED_BUDGET') notes.push(redactMoney(gate.note));

  const base = {
    rangeText: gate?.range ? `Đã đọc tập ${gate.range.from}–${gate.range.to}` : '',
    blocking: [] as ReaderIssueView[], advisory: rawAdvisory, canRun: true, canOverride: false,
    runLabel: (gate && r.state !== 'NONE' ? 'AI đọc lại' : 'AI đọc cả mùa') as 'AI đọc cả mùa' | 'AI đọc lại',
    blocksHandoff: serverBlocks && OPEN_STATES.has(r.state),
    notes, chainNote: CHAIN_NOTE, rewriteWarning: REWRITE_WARNING,
  };
  const withState = (state: SeasonReaderUiState, color: GateColor, label: string, description: string, o: Partial<SeasonReaderCardView> = {}): SeasonReaderCardView =>
    ({ ...base, state, color, label, description, ...o });

  if (!r.enabled) {
    return withState('SKIPPED_OFF', 'default', 'Cổng AI đang tắt', 'Máy chủ đang tắt cổng AI review (ADAPTATION_REVIEW_GATES=off) — không có người đọc, handoff không bị chặn.', { canRun: false, blocksHandoff: false });
  }

  switch (r.state) {
    case 'NONE':
      return withState('NONE', 'default', 'Chưa đọc', `AI chưa đọc cả mùa (tập 1–${drafts}). Bấm "AI đọc cả mùa" trước khi đẩy sang Chapters.`, { runLabel: 'AI đọc cả mùa', advisory: [] });
    case 'RUNNING':
      return withState('RUNNING', 'info', 'Đang đọc', 'AI đang đọc cả mùa ở nền — nút viết kịch bản bị khoá tới khi xong (đang đọc ⇒ viết nhận 409).', { canRun: false, runLabel: 'AI đọc lại' });
    case 'STALE':
      return withState('STALE', 'warning', 'Cũ — kịch bản đã đổi, cần đọc lại',
        `${redactMoney((r.staleReasons ?? []).slice(0, 3).join('; ')) || 'Kịch bản hoặc bible đã đổi từ lần đọc'} — kết quả cũ không còn chặn; bấm "AI đọc lại".`,
        { advisory: [...rawBlocking, ...rawAdvisory], runLabel: 'AI đọc lại' });
    case 'NOT_COVERED':
      return withState('NOT_COVERED', 'warning', `Chưa đọc tới tập ${drafts}`,
        `AI mới đọc tới tập ${r.coveredTo}, nhưng đã có kịch bản tới tập ${drafts}. Bấm "AI đọc lại" để đọc cả mùa.`,
        { advisory: [...rawBlocking, ...rawAdvisory], runLabel: 'AI đọc lại' });
    case 'PASSED':
      return withState('PASSED', 'success', 'Đã qua cổng AI đọc cả mùa', 'AI không thấy lỗi liên tập khách quan (mâu thuẫn trạng thái · biết điều chưa kể · câu treo bị bỏ).');
    case 'BLOCKED':
      return withState('BLOCKED', 'error', `BỊ CHẶN (${rawBlocking.length} lỗi liên tập khách quan)`,
        'Handoff sẽ bị từ chối cho tới khi sửa kịch bản rồi đọc lại, hoặc bấm "Vẫn tiếp tục" kèm lý do.',
        { blocking: rawBlocking, canOverride: true });
    case 'OVERRIDDEN':
      return withState('OVERRIDDEN', 'info', 'Đã vượt cổng', 'Cổng bị bỏ qua có kiểm toán — handoff được phép chạy.', {
        blocking: rawBlocking,
        overrideInfo: gate?.override ? `${gate.override.displayBy?.trim() || 'một quản trị viên'} lúc ${formatVnAuditTime(gate.override.at)} — lý do: "${gate.override.reason}" (${gate.override.issues?.length ?? 0} lỗi bỏ qua)` : undefined,
      });
    case 'FAILED':
      return withState('FAILED', 'warning', 'Chưa kiểm tra được', `Giám khảo AI lỗi — KHÔNG chặn. Có thể thử lại.${gate?.error ? ` (${redactMoney(gate.error)})` : ''}`);
    case 'SKIPPED_BUDGET':
      return withState('SKIPPED_BUDGET', 'warning', 'Đã đạt giới hạn AI', `Đã đạt giới hạn AI cho các lượt chấm — không đọc thêm; handoff đi tiếp KHÔNG có người đọc.`, { canRun: false });
    default:
      // Trạng thái lạ từ server mới hơn FE ⇒ trung tính, không chặn.
      return withState(r.state, 'default', String(r.state ?? 'Chưa rõ'), 'Trạng thái cổng không nhận ra.', { canRun: false, canOverride: false, blocksHandoff: false });
  }
};
